import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import type { RfmOrderBy, RfmOrderByDirection, RfmSegmentCompaniesItem } from "@/types/rfm";
import { segmentIcons } from "./rfmSegmentIcons";

const columns: { orderBy: RfmOrderBy; label: string; alignRight?: boolean }[] = [
  { orderBy: "Code", label: "Code" },
  { orderBy: "CustomerName", label: "Customer name" },
  { orderBy: "NumberOfOrders", label: "No. of orders", alignRight: true },
  { orderBy: "LastTransaction", label: "Last transaction" },
  { orderBy: "TotalValue", label: "Total value", alignRight: true },
  { orderBy: "PercentOfSegmentSales", label: "% of segment sales", alignRight: true },
];

type SelectedSegmentCustomersProps = {
  title: string;
  segmentName?: string;
  description: string;
  customers: RfmSegmentCompaniesItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  orderBy: RfmOrderBy | null;
  orderByDirection: RfmOrderByDirection | null;
  onPageChange: (pageNumber: number) => void;
  onSortChange: (orderBy: RfmOrderBy) => void;
};

const currency = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

type PagerButtonProps = {
  label: string;
  disabled: boolean;
  onClick: () => void;
  active?: boolean;
  first?: boolean;
  last?: boolean;
};

const PagerButton = ({ label, disabled, onClick, active = false, first = false, last = false }: PagerButtonProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    className={`border border-slate-200 px-5 py-2 text-base ${first ? "rounded-l-md" : "-ml-px"} ${last ? "rounded-r-md" : ""} ${active ? "border-blue-600 bg-blue-600 text-white" : disabled ? "bg-white text-slate-400" : "bg-white text-blue-600 hover:bg-slate-50"}`}
  >
    {label}
  </button>
);

export const SelectedSegmentCustomers = ({ title, segmentName, description, customers, totalCount, pageNumber, pageSize, orderBy, orderByDirection, onPageChange, onSortChange }: SelectedSegmentCustomersProps) => {
  const segmentIcon = segmentName ? segmentIcons[segmentName] : undefined;
  const totalValue = customers.reduce((total, customer) => total + customer.totalValue1, 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = pageNumber;
  const startIndex = (currentPage - 1) * pageSize;
  const pageCustomers = customers;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="flex items-center gap-3 text-xl font-semibold text-slate-800">
        {segmentIcon && <FontAwesomeIcon icon={segmentIcon} fixedWidth aria-hidden />}
        <span>{title}</span>
      </h2>
      <p className="mt-2 text-sm text-slate-600">{description}</p>
      {customers.length === 0 ? (
        <p className="mt-6 text-sm text-slate-600">
          {segmentName ? `There are no ${segmentName} customers` : "There are no customers in this segment"}
        </p>
      ) : (
      <div className="mt-6 overflow-x-auto">
        <table className="w-full border-collapse text-sm tabular-nums">
          <thead>
            <tr>
              {columns.map(column => {
                const active = orderBy === column.orderBy;
                return (
                  <th key={column.orderBy} aria-sort={active ? (orderByDirection === "Asc" ? "ascending" : "descending") : "none"} className={`px-4 py-3 font-semibold text-slate-700 ${column.alignRight ? "text-right" : "text-left"}`}>
                    <button type="button" className={`flex items-center gap-2 hover:underline ${column.alignRight ? "ml-auto" : ""}`} onClick={() => onSortChange(column.orderBy)}>
                      <span>{column.label}</span>
                      {active && <FontAwesomeIcon icon={orderByDirection === "Asc" ? faSortUp : faSortDown} aria-hidden />}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageCustomers.map(customer => (
              <tr key={customer.companyId} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-700">
                  <a href={`/Dashboard.aspx?customerid=${encodeURIComponent(customer.companyId)}`} className="text-blue-600 hover:underline">{customer.accountNo}</a>
                </td>
                <td className="px-4 py-3 text-slate-600">{customer.companyName}</td>
                <td className="px-4 py-3 text-right text-slate-600">{customer.uniqueOrderCount}</td>
                <td className="px-4 py-3 text-slate-600">{formatDate(customer.mostRecentTransDate)}</td>
                <td className="px-4 py-3 text-right text-slate-600">{currency.format(customer.totalValue1)}</td>
                <td className="px-4 py-3 text-right text-slate-600">{totalValue > 0 ? `${((customer.totalValue1 / totalValue) * 100).toFixed(1)}%` : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {customers.length > 0 && (
        <nav aria-label="Customer pages" className="mt-6 flex flex-col items-center gap-3">
          <p className="text-lg font-semibold text-slate-800">
            Displaying {startIndex + 1} - {startIndex + pageCustomers.length} of {totalCount} results
          </p>
          <div className="flex">
            <PagerButton label="First" first disabled={currentPage === 1} onClick={() => onPageChange(1)} />
            <PagerButton label="Previous" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} />
            <PagerButton label={`${currentPage} of ${totalPages}`} active disabled={false} onClick={() => undefined} />
            <PagerButton label="Next" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} />
            <PagerButton label="Last" last disabled={currentPage === totalPages} onClick={() => onPageChange(totalPages)} />
          </div>
        </nav>
      )}
    </section>
  );
};
